import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './providers/SupabaseProvider';
import SubscriptionGuard from './components/SubscriptionGuard';
import WorkflowFeatureGuard from './components/WorkflowFeatureGuard';
import FeatureAccessGuard from './components/FeatureAccessGuard';
import SampleAccessGuard from './components/SampleAccessGuard';
import { ACCOUNTING_FEATURE_ENABLED } from './constants/features';
import { buildCustomerPortalUrl, buildCustomerPortalVacationUrl } from './utils/customerPortalLinks';

// Public Pages
const AppointmentRequestPage = React.lazy(() => import('./pages/public/AppointmentRequestPage'));
const AppointmentCancellationPage = React.lazy(() => import('./pages/public/AppointmentCancellationPage'));
const AppointmentConfirmationPage = React.lazy(() => import('./pages/public/AppointmentConfirmationPage'));
const SampleOrderConfirmationPage = React.lazy(() => import('./pages/public/SampleOrderConfirmationPage'));
const CustomerPortalPage = React.lazy(() => import('./pages/public/CustomerPortalPage'));
const ContractRequestPage = React.lazy(() => import('./pages/public/ContractRequestPage'));
const ContractSignaturePage = React.lazy(() => import('./pages/public/ContractSignaturePage'));
const CallbackRequestPage = React.lazy(() => import('./pages/public/CallbackRequestPage'));
const TrialSignupPage = React.lazy(() => import('./pages/public/TrialSignupPage'));
const ReportVerificationPage = React.lazy(() => import('./pages/public/ReportVerificationPage'));
const CertificateVerificationPage = React.lazy(() => import('./pages/public/CertificateVerificationPage'));
const InitialConsultationPage = React.lazy(() => import('./pages/public/InitialConsultationPage'));
const QueueCheckInPage = React.lazy(() => import('./pages/queue/QueueCheckInPage'));
const QueueDisplayPage = React.lazy(() => import('./pages/queue/QueueDisplayPage'));
const QueueStaffPage = React.lazy(() => import('./pages/queue/QueueStaffPage'));
const InitialConsultationWaitingRoomPage = React.lazy(
  () => import('./pages/queue/InitialConsultationWaitingRoomPage'),
);
const InitialConsultationStaffCallPage = React.lazy(
  () => import('./pages/queue/InitialConsultationStaffCallPage'),
);
const MpuTemplatesPage = React.lazy(() => import('./pages/mpu/TemplatesPage'));
const MpuReportEditorPage = React.lazy(() => import('./pages/mpu/ReportEditor'));
const MpuReportViewPage = React.lazy(() => import('./pages/mpu/ReportViewPage'));
const MpuPsychReportPage = React.lazy(() => import('./pages/mpu/PsychReportPage'));
const MpuMedicalReportPage = React.lazy(() => import('./pages/mpu/MedicalReportPage'));
const MpuBlocksPage = React.lazy(() => import('./pages/mpu/BlocksPage'));
const MpuReportsListPage = React.lazy(() => import('./pages/mpu/ReportsListPage'));
const MpuReportDetailPage = React.lazy(() => import('./pages/mpu/ReportDetailPage'));

// Layouts
const AuthLayout = React.lazy(() => import('./layouts/AuthLayout'));
const DashboardLayout = React.lazy(() => import('./layouts/DashboardLayout'));

// Auth Pages
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const SetupPasswordPage = React.lazy(() => import('./pages/auth/SetupPasswordPage'));

// Dashboard Pages
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const RequestsDashboardPage = React.lazy(() => import('./pages/dashboard/RequestsDashboardPage'));
const SamplesMonitoringDashboardPage = React.lazy(() => import('./pages/dashboard/SamplesMonitoringDashboardPage'));
const FinanceDashboardPage = React.lazy(() => import('./pages/dashboard/FinanceDashboardPage'));
const GutachtenDashboardPage = React.lazy(() => import('./pages/dashboard/GutachtenDashboardPage'));
const DocumentsQualityDashboardPage = React.lazy(() => import('./pages/dashboard/DocumentsQualityDashboardPage'));
const SettingsPage = React.lazy(() => import('./pages/dashboard/SettingsPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/dashboard/AdminDashboardPage'));
const NewsPage = React.lazy(() => import('./pages/news/NewsPage'));
const NewsFormPage = React.lazy(() => import('./pages/news/NewsFormPage'));
const PatchNotesPage = React.lazy(() => import('./pages/dashboard/PatchNotesPage'));
const AnalyticsDashboard = React.lazy(() => import('./pages/analytics/AnalyticsDashboard'));
const Bugreports = React.lazy(() => import('./pages/admin/BugReportsPage'));
const DeviceActivationCodesAdminPage = React.lazy(() => import('./pages/admin/DeviceActivationCodesAdminPage'));
const Datenschutz = React.lazy(() => import('./pages/dashboard/components/datenschutz'))
const Agb = React.lazy(() => import('./pages/dashboard/components/agb'))
const AppInfoPage = React.lazy(() => import('./pages/dashboard/components/AppInfoPage'))
const TemperaturePage = React.lazy(() => import('./pages/temperature/TemperaturePage'))
const MaterialInventoryPage = React.lazy(() => import('./pages/qms/MaterialInventoryPage'))
const InspectionManagementPage = React.lazy(() => import('./pages/qms/InspectionManagementPage'))
const PrivacyIncidentsPage = React.lazy(() => import('./pages/qms/PrivacyIncidentsPage'))
const RestoreTestProtocolsPage = React.lazy(() => import('./pages/qms/RestoreTestProtocolsPage'))
const DeceptionSuspicionsPage = React.lazy(() => import('./pages/qms/DeceptionSuspicionsPage'))

//Costomers Pages
const CustomerListPage = React.lazy(() => import('./pages/customers/CustomerListPage'));
const CustomerDetailsPage = React.lazy(() => import('./pages/customers/CustomerDetailsPage'));
const CustomerFormPage = React.lazy(() => import('./pages/customers/CustomerFormPage'));
const CustomerEditPage = React.lazy(() => import('./pages/customers/CustomerEditPage'));
const AnamnesisInterviewPage = React.lazy(() => import('./pages/customers/AnamnesisInterviewPage'));
const PreparationContactsPage = React.lazy(() => import('./pages/customers/PreparationContactsPage'));
const AssessmentContactsPage = React.lazy(() => import('./pages/customers/AssessmentContactsPage'));
const PostalShipmentsPage = React.lazy(() => import('./pages/customers/PostalShipmentsPage'));

//Abstienzn-management
const ContractTypesPage = React.lazy(() => import('./pages/abstinenz-management/ContractTypesPage'));
const AbstinenzVertragPage = React.lazy(() => import('./pages/abstinenz-management/AbstinenzVertragPage'));
const SampleCalculationPage = React.lazy(() => import('./pages/abstinenz-management/SampleCalculationPage'));
const SampleCalculationDemo = React.lazy(() => import('./pages/abstinenz-management/SampleCalculationDemo'));
const SubstanceGroupsPage = React.lazy(() => import('./pages/abstinenz-management/SubstanceGroupsPage'));
const ContractListPage = React.lazy(() => import('./pages/abstinenz-management/ContractListPage'));
const SamplesCalendarPage = React.lazy(() => import('./pages/samples/SamplesCalendarPage'));
import SamplesPageComponent from './pages/samples/samplespage';
const PendingResultsPage = React.lazy(() => import('./pages/samples/PendingResultsPage'));
const OverdueSamplesPage = React.lazy(() => import('./pages/samples/OverdueSamplesPage'));
const SamplePlanningViolationsPage = React.lazy(() => import('./pages/samples/SamplePlanningViolationsPage'));
const ContractDetailsPage = React.lazy(() => import('./pages/abstinenz-management/ContractDetailsPage'));
const ContractCustomerSignaturePage = React.lazy(
  () => import('./pages/abstinenz-management/ContractCustomerSignaturePage'),
);
const ContractCreationPage = React.lazy(() => import('./pages/abstinenz-management/ContractCreationPage'));
const ContractTemplatesPage = React.lazy(() => import('./pages/abstinenz-management/ContractTemplatesPage'));
const ContractTemplateEditPage = React.lazy(() => import('./pages/abstinenz-management/ContractTemplateEditPage'));
const CertificateTemplatesPage = React.lazy(
  () => import('./pages/abstinenz-management/CertificateTemplatesPage'),
);
const ContractCertificatesPage = React.lazy(
  () => import('./pages/abstinenz-management/ContractCertificatesPage'),
);
const WorkflowPage = React.lazy(() => import('./pages/abstinenz-management/WorkflowPage'));
const CallbackRequestsPage = React.lazy(
  () => import('./pages/abstinenz-management/CallbackRequestsPage'),
);
const ContractRequestsPage = React.lazy(
  () => import('./pages/abstinenz-management/ContractRequestsPage'),
);
const VacationListPage = React.lazy(() => import('./pages/abstinenz-management/VacationListPage'));
const CalendarPage = React.lazy(() => import('./pages/samples/CalendarPage'));
const VoucherPage = React.lazy(() => import('./pages/accounting/VoucherPage'));
const InvoiceListPage = React.lazy(() => import('./pages/accounting/InvoiceListPage'));
const InvoiceFormPage = React.lazy(() => import('./pages/accounting/InvoiceFormPage'));
const RSCodeWerkInvoiceFormPage = React.lazy(() => import('./pages/accounting/RSCodeWerkInvoiceFormPage'));
const TasksPage = React.lazy(() => import('./pages/tasks/TasksPage'));
const DeadlinesNotificationsPage = React.lazy(() => import('./pages/tasks/DeadlinesNotificationsPage'));
const ProductlistPage = React.lazy(() => import('./pages/accounting/ProductlistPage'));
const CashbookPage = React.lazy(() => import('./pages/accounting/CashbookPage'));
const CashbookClosingsPage = React.lazy(() => import('./pages/accounting/CashbookClosingsPage'));
const Kündigungsschreiben = React.lazy(() => import ('./pages/abstinenz-management/kundigungPage'));
const FormUploadPage = React.lazy(() => import('./pages/abstinenz-management/FormUploadPage'));
const FormTemplateGuide = React.lazy(() => import('./pages/abstinenz-management/FormTemplateGuide'));
const MarketingPage = React.lazy(() => import('./pages/marketing/MarketingPage'));
const QMSDashboard = React.lazy(() => import('./pages/qms/QMSDashboard'));
const QMManualDashboardPage = React.lazy(() => import('./pages/qms/QMManualDashboard'));
const QMManualDetailPage = React.lazy(() => import('./pages/qms/QMManualDetailPage'));
const StorageManagementPage = React.lazy(() => import('./pages/storage/StorageManagementPage'));


// internal-management
const SubscriptionSuccessPage = React.lazy(() => import('./pages/internal-management/SubscriptionSuccessPage'));
const SubscriptionPage = React.lazy(() => import('./pages/internal-management/SubscriptionPage'));
const UserManagementPage = React.lazy(() => import('./pages/internal-management/UserManagementPage'));
const AppointmentManagementPage = React.lazy(() => import('./pages/internal-management/AppointmentManagementPage'));
const AppointmentSettingsPage = React.lazy(() => import('./pages/internal-management/AppointmentSettingsPage'));
const ContractRequestSettingsPage = React.lazy(() => import('./pages/internal-management/ContractRequestSettingsPage'));
const OrganizationSettingsPage = React.lazy(() => import('./pages/internal-management/OrganizationSettingsPage'));
const OrganizationDevicesPage = React.lazy(() => import('./pages/internal-management/OrganizationDevicesPage'));
const OrganizationLocationsPage = React.lazy(() => import('./pages/internal-management/OrganizationLocationsPage'));
const OrganizationFilesPage = React.lazy(() => import('./pages/internal-management/OrganizationFilesPage'));
const EmployeeFilesPage = React.lazy(() => import('./pages/internal-management/EmployeeFilesPage'));
const PersonalPlanningPage = React.lazy(() => import('./pages/internal-management/PersonalPlanningPage'));
const OrganizationCertificatesPage = React.lazy(
  () => import('./pages/internal-management/OrganizationCertificatesPage'),
);
const PortalUploadsPage = React.lazy(() => import('./pages/internal-management/PortalUploadsPage'));
const EmailTemplateEditor = React.lazy(() => import('./pages/internal-management/EmailTemplateEditorPage'));
const LetterTemplatesPage = React.lazy(() => import('./pages/internal-management/LetterTemplatesPage'));
const SmsTemplatesPage = React.lazy(() => import('./pages/internal-management/SmsTemplatesPage'));
const WhatsAppTemplatesPage = React.lazy(() => import('./pages/internal-management/WhatsAppTemplatesPage'));
const CityscreenManagementPage = React.lazy(() => import('./pages/internal-management/CityscreenManagementPage'));
// Error Pages
const SeiteimBauPage = React.lazy(() => import('./pages/error/SeiteimBauPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
  </div>
);

const HAS_SEPARATE_CUSTOMER_PORTAL = Boolean(
  (import.meta.env.VITE_CUSTOMER_PORTAL_BASE_URL as string | undefined)?.trim(),
);

function withSearchParams(targetUrl: string, search: string): string {
  if (!search) {
    return targetUrl;
  }

  const separator = targetUrl.includes('?') ? '&' : '?';
  return `${targetUrl}${separator}${search.replace(/^\?/, '')}`;
}

function CustomerPortalRouteBridge() {
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();

  React.useEffect(() => {
    if (!HAS_SEPARATE_CUSTOMER_PORTAL) {
      return;
    }

    const redirectTarget = withSearchParams(
      buildCustomerPortalUrl({
        slug,
        origin: window.location.origin,
      }),
      location.search,
    );

    window.location.replace(redirectTarget);
  }, [location.search, slug]);

  if (!HAS_SEPARATE_CUSTOMER_PORTAL) {
    return <CustomerPortalPage />;
  }

  return <LoadingSpinner />;
}

function CustomerPortalVacationRouteBridge() {
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();

  React.useEffect(() => {
    if (!HAS_SEPARATE_CUSTOMER_PORTAL) {
      return;
    }

    const redirectTarget = withSearchParams(
      buildCustomerPortalVacationUrl({
        slug,
        origin: window.location.origin,
      }),
      location.search,
    );

    window.location.replace(redirectTarget);
  }, [location.search, slug]);

  if (!HAS_SEPARATE_CUSTOMER_PORTAL) {
    return <CustomerPortalPage />;
  }

  return <LoadingSpinner />;
}

export default function AppRoutes() {
  const { user } = useAuth();

  return (
    <React.Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public routes (no auth layout - for anonymous users) */}
        <Route path="/kundenportal" element={<CustomerPortalRouteBridge />} />
        <Route path="/kundenportal/:slug" element={<CustomerPortalRouteBridge />} />
        <Route path="/vacation-request/:slug" element={<CustomerPortalVacationRouteBridge />} />
        <Route path="/urlaubbeantragen/:slug" element={<CustomerPortalVacationRouteBridge />} />
        <Route path="/vertrag-anfragen/:slug" element={<ContractRequestPage />} />
        <Route path="/vertrag-signieren/:token" element={<ContractSignaturePage />} />
        <Route path="/rueckruf/:slug" element={<CallbackRequestPage />} />
        <Route path="/termin/:slug" element={<AppointmentRequestPage />} />
        <Route path="/appointment/cancel/:token" element={<AppointmentCancellationPage />} />
        <Route path="/appointment/confirm/:token" element={<AppointmentConfirmationPage />} />
        <Route path="/einbestellung/:slug/:token" element={<SampleOrderConfirmationPage />} />
        <Route path="/testabo" element={<TrialSignupPage />} />
        <Route path="/verify-report" element={<ReportVerificationPage />} />
        <Route path="/verify-certificate/:verificationId" element={<CertificateVerificationPage />} />
        <Route path="/erstgespraech/:slug/:token" element={<InitialConsultationPage />} />
        <Route path="/aufruf/:slug/anmeldung" element={<QueueCheckInPage />} />
        <Route path="/aufruf/:slug/anzeige" element={<QueueDisplayPage />} />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/dashboard" />} />
          <Route path="/register" element={!user ? <RegisterPage /> : <Navigate to="/dashboard" />} />
          <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />} />
          <Route path="/setup-password" element={<SetupPasswordPage />} />
          <Route path="/agb" element={<Agb />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/roadmap" element={<AppInfoPage />} />
        </Route>

        {/* Dashboard routes */}
        <Route element={<DashboardLayout />}>
          <Route
            path="/dashboard"
            element={
              user ? (
                user.user_metadata?.redirectTo === 'setup-password' ? (
                  <Navigate to="/setup-password\" replace />
                ) : (
                  <SubscriptionGuard>
                    <DashboardPage />
                  </SubscriptionGuard>
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user ? (
                <SubscriptionGuard>
                  <SettingsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard/anfragen"
            element={
              user ? (
                <SubscriptionGuard>
                  <RequestsDashboardPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard/proben-monitoring"
            element={
              user ? (
                <SubscriptionGuard>
                  <SamplesMonitoringDashboardPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard/finanzen"
            element={
              user ? (
                <SubscriptionGuard>
                  <FinanceDashboardPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard/gutachten"
            element={
              user ? (
                <SubscriptionGuard>
                  <GutachtenDashboardPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard/dokumente-qualitaet"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <WorkflowFeatureGuard>
                      <DocumentsQualityDashboardPage />
                    </WorkflowFeatureGuard>
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/portal-uploads"
            element={
              user ? (
                <SubscriptionGuard>
                  <PortalUploadsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/aufrufsystem"
            element={
              user ? (
                <SubscriptionGuard>
                  <QueueStaffPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/cityscreen-management"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <CityscreenManagementPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/erstgespraech-wartezimmer"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <InitialConsultationWaitingRoomPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/erstgespraech-call/:consultationId"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <InitialConsultationStaffCallPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/patchnotes"
            element={
              user ? (
                <SubscriptionGuard>
                  <PatchNotesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/blocks"
            element={
              user ? (
                <SubscriptionGuard>
                  <MpuBlocksPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/reports"
            element={
              user ? (
                <SubscriptionGuard>
                  <MpuReportsListPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/reports/:id"
            element={
              user ? (
                <SubscriptionGuard>
                  <MpuReportDetailPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/emailtemplates"
            element={
              user ? (
                <SubscriptionGuard>
                  <EmailTemplateEditor />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/lettertemplates"
            element={
              user ? (
                <SubscriptionGuard>
                  <LetterTemplatesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sms-templates"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <SmsTemplatesPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/whatsapp-templates"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <WhatsAppTemplatesPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/analytics/*"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_analytics">
                    <AnalyticsDashboard />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/temperature"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <WorkflowFeatureGuard>
                      <TemperaturePage />
                    </WorkflowFeatureGuard>
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/materials"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <WorkflowFeatureGuard>
                      <MaterialInventoryPage />
                    </WorkflowFeatureGuard>
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/storage"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_advanced_functions">
                    <StorageManagementPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/organization-settings"
            element={
              user ? (
                <SubscriptionGuard>
                  <OrganizationSettingsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/appointment-settings"
            element={
              user ? (
                <SubscriptionGuard>
                  <AppointmentSettingsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contract-request-settings"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <ContractRequestSettingsPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/organization-users"
            element={
              user ? (
                <SubscriptionGuard>
                  <UserManagementPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/organization-devices"
            element={
              user ? (
                <SubscriptionGuard>
                  <OrganizationDevicesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/organization-locations"
            element={
              user ? (
                <SubscriptionGuard>
                  <OrganizationLocationsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/organization-files"
            element={
              user ? (
                <SubscriptionGuard>
                  <OrganizationFilesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/employee-files"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_personal">
                    <EmployeeFilesPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/organization-certificates"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <OrganizationCertificatesPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contract-types"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractTypesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/Vertragsbedingungen"
            element={
              user ? (
                <SubscriptionGuard>
                  {/* Vertragsbedingungen werden über erweiterte Funktionen freigeschaltet. */}
                  <FeatureAccessGuard featureKey="feature_advanced_functions">
                    <AbstinenzVertragPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/Kündigungsschreiben"
            element={
              user ? (
                <SubscriptionGuard>
                  {/* Zugriff auf Kündigungsschreiben wird über das Anpassungen-Feature gesteuert. */}
                  <FeatureAccessGuard featureKey="feature_customizations">
                    <Kündigungsschreiben />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractListPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/new"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractCreationPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/:id"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractDetailsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/personal-planning"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_personal">
                    <PersonalPlanningPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/:id/sign/customer"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractCustomerSignaturePage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/:id/certificates"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractCertificatesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/vacation-requests"
            element={
              user ? (
                <SubscriptionGuard>
                  <VacationListPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/templates"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractTemplatesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/certificates/templates"
            element={
              user ? (
                <SubscriptionGuard>
                  <CertificateTemplatesPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/admin/bug-reports"
            element={
              user ? (
                <SubscriptionGuard>
                  <Bugreports />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contracts/templates/:id/edit"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractTemplateEditPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sample-calculation"
            element={
              user ? (
                <SubscriptionGuard>
                  {/* Probenberechnung ist Teil der erweiterten Funktionen. */}
                  <FeatureAccessGuard featureKey="feature_advanced_functions">
                    <SampleCalculationPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/sample-calculation-demo"
            element={
              user ? (
                <SubscriptionGuard>
                  {/* Die Demo nutzt dieselbe Freigabe wie die reguläre Probenberechnung. */}
                  <FeatureAccessGuard featureKey="feature_advanced_functions">
                    <SampleCalculationDemo />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/samples-calendar"
            element={
              user ? (
                <SubscriptionGuard>
                  <SampleAccessGuard>
                  <SamplesCalendarPage />
                </SampleAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/samples"
            element={
              user ? (
                <SubscriptionGuard>
                  <SampleAccessGuard>
                  <SamplesPageComponent />
                </SampleAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/samples/pending"
            element={
              user ? (
                <SubscriptionGuard>
                  <SampleAccessGuard allowAssistant>
                    <PendingResultsPage />
                  </SampleAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/samples/overdue"
            element={
              user ? (
                <SubscriptionGuard>
                  <SampleAccessGuard>
                  <OverdueSamplesPage />
                </SampleAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/substance-groups"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_advanced_functions">
                    <SubstanceGroupsPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/samples/planning-violations"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_advanced_functions">
                    <SampleAccessGuard>
                      <SamplePlanningViolationsPage />
                    </SampleAccessGuard>
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        <Route
           path="/tasks"
           element={
             user ? (
               <SubscriptionGuard>
                 <TasksPage />
               </SubscriptionGuard>
             ) : (
               <Navigate to="/login" />
             )
           }
        />
        <Route
          path="/deadlines-notifications"
          element={
            user ? (
              <SubscriptionGuard>
                <DeadlinesNotificationsPage />
              </SubscriptionGuard>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
          <Route
            path="/qms/manuals"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_qm_manuals">
                    <WorkflowFeatureGuard>
                      <QMManualDashboardPage />
                    </WorkflowFeatureGuard>
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/qms/manuals/:manualId"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_qm_manuals">
                    <WorkflowFeatureGuard>
                      <QMManualDetailPage />
                    </WorkflowFeatureGuard>
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
        />
        <Route
          path="/qms/inspections"
          element={
            user ? (
              <SubscriptionGuard>
                <FeatureAccessGuard featureKey="feature_management">
                  <WorkflowFeatureGuard>
                    <InspectionManagementPage />
                  </WorkflowFeatureGuard>
                </FeatureAccessGuard>
              </SubscriptionGuard>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/qms/privacy-incidents"
          element={
            user ? (
              <SubscriptionGuard>
                <FeatureAccessGuard featureKey="feature_management">
                  <WorkflowFeatureGuard>
                    <PrivacyIncidentsPage />
                  </WorkflowFeatureGuard>
                </FeatureAccessGuard>
              </SubscriptionGuard>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/qms/restore-tests"
          element={
            user ? (
              <SubscriptionGuard>
                <FeatureAccessGuard featureKey="feature_management">
                  <WorkflowFeatureGuard>
                    <RestoreTestProtocolsPage />
                  </WorkflowFeatureGuard>
                </FeatureAccessGuard>
              </SubscriptionGuard>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/qms/deception-suspicions"
          element={
            user ? (
              <SubscriptionGuard>
                <FeatureAccessGuard featureKey="feature_management">
                  <WorkflowFeatureGuard>
                    <DeceptionSuspicionsPage />
                  </WorkflowFeatureGuard>
                </FeatureAccessGuard>
              </SubscriptionGuard>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
           path="/qms"
           element={
             user ? (
               <SubscriptionGuard>
                 <WorkflowFeatureGuard>
                   <QMSDashboard />
                 </WorkflowFeatureGuard>
               </SubscriptionGuard>
             ) : (
               <Navigate to="/login" />
             )
           }
        />
         <Route
            path="/calendar"
            element={
              user ? (
                <SubscriptionGuard>
                  <CalendarPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/news"
            element={
              user ? (
                <SubscriptionGuard>
                  <NewsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/news/new"
            element={
              user ? (
                <SubscriptionGuard>
                  <NewsFormPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/form-upload"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <FormUploadPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/workflow"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <WorkflowPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/callback-requests"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <CallbackRequestsPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/contract-requests"
            element={
              user ? (
                <SubscriptionGuard>
                  <ContractRequestsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/forms"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <FormUploadPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/templates"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <MpuTemplatesPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/report-editor"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <MpuReportEditorPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/reports/:reportId"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <MpuReportViewPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/reports/:reportId/psych"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <MpuPsychReportPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mpu/reports/:reportId/medical"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <MpuMedicalReportPage />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/forms/guide"
            element={
              user ? (
                <SubscriptionGuard>
                  <WorkflowFeatureGuard>
                    <FormTemplateGuide />
                  </WorkflowFeatureGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          {ACCOUNTING_FEATURE_ENABLED && (
            <>
              <Route
                path="/invoices"
                element={
                  user ? (
                    <SubscriptionGuard>
                      <FeatureAccessGuard featureKey="feature_accounting">
                        <InvoiceListPage />
                      </FeatureAccessGuard>
                    </SubscriptionGuard>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/invoices/new"
                element={
                  user ? (
                    <SubscriptionGuard>
                      <FeatureAccessGuard featureKey="feature_accounting">
                        <InvoiceFormPage />
                      </FeatureAccessGuard>
                    </SubscriptionGuard>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/invoices/new/rs-codewerk"
                element={
                  user ? (
                    <SubscriptionGuard>
                      <FeatureAccessGuard featureKey="feature_accounting">
                        <RSCodeWerkInvoiceFormPage />
                      </FeatureAccessGuard>
                    </SubscriptionGuard>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/invoices/:id/edit"
                element={
                  user ? (
                    <SubscriptionGuard>
                      <FeatureAccessGuard featureKey="feature_accounting">
                        <InvoiceFormPage />
                      </FeatureAccessGuard>
                    </SubscriptionGuard>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/vouchers"
                element={
                  user ? (
                    <SubscriptionGuard>
                      <VoucherPage />
                    </SubscriptionGuard>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/cashbook"
                element={
                  user ? (
                    <SubscriptionGuard>
                      <FeatureAccessGuard featureKey="feature_accounting">
                        <CashbookPage />
                      </FeatureAccessGuard>
                    </SubscriptionGuard>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
              <Route
                path="/cashbook/closings"
                element={
                  user ? (
                    <SubscriptionGuard>
                      <FeatureAccessGuard featureKey="feature_accounting">
                        <CashbookClosingsPage />
                      </FeatureAccessGuard>
                    </SubscriptionGuard>
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
            </>
          )}
          <Route path="/dashboard/subscription" element={user ? <SubscriptionPage /> : <Navigate to="/login" />} />
          <Route
            path="/dashboard/subscription/success"
            element={user ? <SubscriptionSuccessPage /> : <Navigate to="/login" />}
          />
          <Route path="/admin" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/device-activation-codes" element={
            user?.is_super_user ? (
              <DeviceActivationCodesAdminPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/abos" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/organisationen" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/rs-time" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/behacare" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/rs-codewerk" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/monitoring" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/offene-fehler" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/substanzen" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/patchnotes" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/verhandlungen" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route path="/admin/roadmap-bugs-downloads" element={
            user?.is_super_user ? (
              <AdminDashboardPage />
            ) : (
              <Navigate to="/dashboard\" replace />
            )
          } />
          <Route
            path="/customers"
            element={
              user ? (
                <SubscriptionGuard>
                  <CustomerListPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/preparation-contacts"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <PreparationContactsPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/assessment-contacts"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <AssessmentContactsPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/postal-shipments"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <PostalShipmentsPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/new"
            element={
              user ? (
                <SubscriptionGuard>
                  <CustomerFormPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/:id"
            element={
              user ? (
                <SubscriptionGuard>
                  <CustomerDetailsPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/:id/anamnesis/new"
            element={
              user ? (
                <SubscriptionGuard>
                  <AnamnesisInterviewPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/:id/anamnesis/:interviewId"
            element={
              user ? (
                <SubscriptionGuard>
                  <AnamnesisInterviewPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/customers/edit/:id"
            element={
              user ? (
                <SubscriptionGuard>
                  <CustomerEditPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/marketing"
            element={
              user ? (
                <SubscriptionGuard>
                  <FeatureAccessGuard featureKey="feature_management">
                    <MarketingPage />
                  </FeatureAccessGuard>
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/appointments"
            element={
              user ? (
                <SubscriptionGuard>
                  <AppointmentManagementPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/products"
            element={
              user ? (
                <SubscriptionGuard>
                  <ProductlistPage />
                </SubscriptionGuard>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Route>

        {/* Redirect root to dashboard or login */}
        <Route path="/" element={<Navigate to={user ? '/dashboard' : '/login'} />} />

        {/* 404 route */}
        <Route path="*" element={<SeiteimBauPage />} />
      </Routes>
    </React.Suspense>
  );
}

export { AppRoutes };





